import {
  ContentCategoryRow,
  ContentNearVariantGroup,
} from "../../common/contentTypes";

export interface CategoryGroup {
  group: string;
  categories: ContentCategoryRow[];
  questionCount: number;
}

/**
 * Alphabetical within each group. Rank order used to be reflected here too, but
 * the promoted panel now shows the "Popular" order on its own, so duplicating it
 * in the grid only made a category hard to find by name.
 */
export function groupCategories(
  categories: ContentCategoryRow[],
): CategoryGroup[] {
  const byGroup = new Map<string, ContentCategoryRow[]>();
  for (const category of categories) {
    const bucket = byGroup.get(category.group) ?? [];
    bucket.push(category);
    byGroup.set(category.group, bucket);
  }

  return Array.from(byGroup.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([group, rows]) => ({
      group,
      questionCount: rows.reduce((sum, row) => sum + row.questionCount, 0),
      categories: rows.slice().sort((a, b) => a.name.localeCompare(b.name)),
    }));
}

export interface MergeCandidate {
  normalized: string;
  categories: ContentCategoryRow[];
  /** The larger category — merging into it moves the fewest questions. */
  suggestedTarget: ContentCategoryRow;
}

export function mergeCandidates(
  categories: ContentCategoryRow[],
  nearVariants: ContentNearVariantGroup[],
): MergeCandidate[] {
  return nearVariants
    .map((variant) => {
      const matches = variant.names
        .map((name) => categories.find((category) => category.name === name))
        .filter((category): category is ContentCategoryRow => !!category);
      if (matches.length < 2) {
        return undefined;
      }
      const suggestedTarget = matches.reduce((largest, candidate) =>
        candidate.questionCount > largest.questionCount ? candidate : largest,
      );
      return {
        normalized: variant.normalized,
        categories: matches,
        suggestedTarget,
      };
    })
    .filter((candidate): candidate is MergeCandidate => !!candidate);
}
